import { createSelector } from 'selectors'
import axios from 'axios'
import { useEvent, useState } from '@/core'
import { Loader } from '@/component'
import { getDependentSelectBoxComponents } from './dependentSelectBox.selector'

type Options = {
	suggestTimeout: number
	dataLinkName: string
	dataParentsName: string
}

const defaultOptions: Options = {
	suggestTimeout: 350,
	dataLinkName: 'dependentselectbox',
	dataParentsName: 'dependentselectboxParents',
}

type ResponseType = {
	dependentselectbox?: {
		id: string | number
		prompt?: string | false
		disabledWhenEmpty?: boolean
		items: [
			{
				key: string
				value: any
				attributes?: Record<string, any>
			},
		]
		value: string | null
	} | null
}

type State = {
	options: Options
	timeout: Record<string, number | false>
	dependentSelectBoxes: NodeListOf<HTMLSelectElement>
	callback: (() => void) | null
}

export const DependentSelectBoxComponent = (() => {
	const { state, setState } = useState<State>()

	const initHandlers = () => {
		const { dependentSelectBoxes, options } = state

		dependentSelectBoxes.forEach((dependentSelect) => {
			const parents = JSON.parse(dependentSelect.dataset[options.dataParentsName] as string)

			Object.entries(parents).forEach(([, id]) => {
				const parentElement = createSelector<HTMLInputElement>({ selector: `#${id}` })

				if (parentElement === null) {
					return
				}

				if (parentElement.type === 'text' || parentElement.nodeName.toLowerCase() === 'textarea') {
					const event = useEvent(parentElement, 'keyup')
					event.register(() => {
						handleKeyUp(parentElement, dependentSelect)
					})
				} else {
					const event = useEvent(parentElement, 'change')
					event.register(() => {
						handleChange(parentElement, dependentSelect)
					})
				}
			})
		})
	}

	const getParents = (element: HTMLElement): [string, string][] => {
		const { options } = state

		if (!(options.dataParentsName in element.dataset)) {
			return []
		}

		const parents = JSON.parse(element.dataset[options.dataParentsName] as string)

		return Object.entries(parents).flatMap(([name, id]) => {
			const parentElement = document.getElementById(id as string)

			if (parentElement === null) {
				return [[name, id as string]]
			}

			const parentParents = getParents(parentElement)
			parentParents.push([name, id as string])

			return parentParents
		})
	}

	const getSignalLink = (element: HTMLElement) => {
		const { options } = state

		let signalLink = element.dataset[options.dataLinkName]

		if (signalLink === undefined) {
			return null
		}

		const parents = getParents(element)

		parents.forEach(([name, id]) => {
			const paramElement = createSelector<HTMLInputElement>({ selector: `#${id}` })

			if (paramElement === null) {
				return
			}

			let val: any

			if (paramElement.type === 'checkbox') {
				val = paramElement.checked ? 1 : 0
			} else {
				val = paramElement.value
				if (!val) {
					return
				}
			}

			if (val instanceof Array) {
				val.forEach((value) => {
					signalLink += `&${name}[]=${value}`
				})
			} else {
				signalLink += `&${name}=${val}`
			}
		})

		return signalLink
	}

	const process = async (parentElement: HTMLInputElement, dependentSelect: HTMLSelectElement) => {
		// Validate if signalLink exist
		const signalLink = getSignalLink(dependentSelect)
		if (signalLink === null) {
			return
		}
		// skip cascaded data loading if parentEl is not yet set, empty dependentSelect & add empty prompt
		const parentElVal = parentElement.value
		if (!parentElVal) {
			while (dependentSelect.firstChild !== null) {
				dependentSelect.firstChild.remove()
			}

			let option = document.createElement('option')
			option.value = ''
			option = dependentSelect.appendChild(option)

			option.dispatchEvent(new window.Event('change'))
			return
		}

		const { form } = parentElement

		if (form !== null) {
			Loader.create(form, { absolute: true })
		}

		await axios.get(signalLink, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })

		if (form !== null) {
			Loader.destroy(form)
		}
	}

	const handleChange = async (parentElement: HTMLInputElement, dependentSelect: HTMLSelectElement) => {
		await process(parentElement, dependentSelect)
	}

	const handleKeyUp = async (parentElement: HTMLInputElement, dependentSelect: HTMLSelectElement) => {
		const { timeout, options } = state
		// reset timeout
		const timeoutKey = dependentSelect.id

		const timeoutHandle = timeout[timeoutKey]
		if (timeoutHandle !== undefined && timeoutHandle !== false) {
			clearTimeout(timeoutHandle)
		}

		timeout[timeoutKey] = window.setTimeout(() => {
			process(parentElement, dependentSelect)
		}, options.suggestTimeout)
	}

	const updateOptions = (payload: ResponseType) => {
		const data = payload.dependentselectbox
		if (data === null || data === undefined) {
			return
		}

		const select = createSelector<HTMLSelectElement>({ selector: `#${data.id}` })
		while (select.firstChild !== null) {
			select.firstChild.remove()
		}

		// prompt set
		if (data.prompt !== false && data.prompt !== undefined) {
			const option = document.createElement('option')
			option.value = ''
			option.text = data.prompt

			select.appendChild(option)
		}

		const { items } = data

		if (items.length > 0) {
			// set disabled, when control haven not choices
			if (data.disabledWhenEmpty === true) {
				select.disabled = false
			}

			data.items.forEach((item) => {
				if (typeof item.value === 'object') {
					const itemValue: Record<string, { value: string }> = item.value

					const optGroup = document.createElement('optgroup')
					optGroup.label = item.key

					Object.entries(itemValue).forEach(([objI, objItem]) => {
						const option = document.createElement('option')
						option.value = objI
						option.text = objItem.value

						if (data.value !== null && objI === data.value) {
							option.selected = true
						}

						optGroup.appendChild(option)
					})

					select.appendChild(optGroup)
				} else {
					const option = document.createElement('option')
					option.value = item.key
					option.text = item.value

					if (item.attributes !== undefined) {
						Object.entries(item.attributes).forEach(([attr, attrValue]) => {
							option.setAttribute(attr, attrValue)
						})
					}

					if (data.value !== null && item.key === data.value) {
						option.selected = true
					}

					select.appendChild(option)
				}
			})
		} else if (data.disabledWhenEmpty) {
			select.disabled = true
		}

		M.FormSelect.init(select)

		select.dispatchEvent(new window.Event('change'))

		const { callback } = state
		if (callback !== null) {
			callback()
		}
	}

	const init = (
		isAjax: boolean,
		payload: any,
		options: Partial<Options> = {},
		callback: (() => void) | null = null,
	) => {
		if (isAjax) {
			if (payload.data === undefined || payload.data === null) return
			updateOptions(payload.data)
			return
		}
		setState({
			options: {
				...defaultOptions,
				...options,
			},
			timeout: {},
			...getDependentSelectBoxComponents(),
			callback,
		})

		initHandlers()
	}

	return {
		init,
	}
})()
